import type { Query } from "@rocicorp/zero";
import {
  type CommitConnectorUsage,
  type ConnectedAppUsage,
  type Schema,
  zql,
} from "./generatedDrizzleSchema";

export const commitStepUsages = (
  u: Query<"connectedAppUsages", Schema, ConnectedAppUsage>
) => u.where("type", "commit_step");

export function liveConnectorUsageRollupsBySpaceQuery(
  spaceIds: readonly string[],
  usages: (
    u: Query<"commitConnectorUsages", Schema, CommitConnectorUsage>
  ) => Query<"commitConnectorUsages", Schema, CommitConnectorUsage> = (u) => u
) {
  return zql.workflowBranches
    .whereExists("workflow", (workflow) =>
      workflow.where("deletedAt", "IS", null).where("spaceId", "IN", spaceIds)
    )
    .whereExists("headConnectorUsages", usages)
    .related("workflow")
    .related("headConnectorUsages", usages);
}

// The reverse direction: one connector's live usages, with the branch-head
// workflows that run them. The bare exists prunes the superseded commits every
// edit leaves behind; deleted-workflow heads hydrate a null workflow for the
// caller to skip.
export function liveUsagesOfConnectorQuery(connectedAppId: string) {
  return commitStepUsages(zql.connectedAppUsages)
    .where("connectedAppId", connectedAppId)
    .whereExists("branchesAtHead")
    .related("branchesAtHead", (b) =>
      b.related("workflow", (w) => w.where("deletedAt", "IS", null))
    );
}

export function liveConnectorIdsBySpace(
  branches: readonly {
    workflow?: { spaceId: string } | null;
    headConnectorUsages: readonly { connectorId: string }[];
  }[]
): Map<string, Set<string>> {
  const bySpace = new Map<string, Set<string>>();
  for (const { workflow, headConnectorUsages } of branches) {
    if (!workflow) continue;
    for (const { connectorId } of headConnectorUsages)
      bySpace.set(
        workflow.spaceId,
        (bySpace.get(workflow.spaceId) ?? new Set()).add(connectorId)
      );
  }
  return bySpace;
}

type SpaceConnectorUsages = readonly {
  id: string;
  workflows: readonly {
    id: string;
    connectorUsages: readonly { connectorId: string }[];
  }[];
}[];

export function workflowIdsByConnector(
  spaces: SpaceConnectorUsages
): Map<string, Set<string>> {
  const byConnector = new Map<string, Set<string>>();
  for (const { workflows } of spaces)
    for (const { id, connectorUsages } of workflows)
      for (const { connectorId } of connectorUsages)
        byConnector.set(
          connectorId,
          (byConnector.get(connectorId) ?? new Set()).add(id)
        );
  return byConnector;
}

export function connectorIdsBySpace(
  spaces: SpaceConnectorUsages
): Map<string, Set<string>> {
  return new Map(
    spaces.map(({ id, workflows }) => [
      id,
      new Set(
        workflows.flatMap((w) => w.connectorUsages.map((u) => u.connectorId))
      ),
    ])
  );
}
